'use client'

import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from 'class-variance-authority'
import { useTranslations } from 'next-intl'
import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { InfoIcon } from '@phosphor-icons/react/dist/ssr/Info'
import { WarningCircleIcon } from '@phosphor-icons/react/dist/ssr/WarningCircle'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'

type StatusBannerVariant = 'info' | 'warning' | 'success'

type StatusBannerProps = {
  variant?: StatusBannerVariant
  title?: string
  children: ReactNode
  action?: ReactNode
  onDismiss?: () => void
  emphasis?: boolean
} & HTMLAttributes<HTMLElement>

const ICON_BY_VARIANT: Record<StatusBannerVariant, typeof InfoIcon> = {
  info: InfoIcon,
  warning: WarningCircleIcon,
  success: CheckCircleIcon,
}

const COLOR_BY_VARIANT: Record<
  StatusBannerVariant,
  { container: string; icon: string; emphasisContainer: string; badge: string }
> = {
  info: {
    container: 'border-sky-200/80 bg-sky-50/60',
    icon: 'text-sky-600',
    emphasisContainer: 'border-sky-300 bg-sky-50 ring-1 ring-sky-200/70',
    badge: 'bg-sky-600',
  },
  warning: {
    container: 'border-amber-200/80 bg-amber-50/60',
    icon: 'text-amber-600',
    emphasisContainer: 'border-amber-300 bg-amber-50 ring-1 ring-amber-200/70',
    badge: 'bg-amber-600',
  },
  success: {
    container: 'border-primary-200/80 bg-primary-50/60',
    icon: 'text-primary-600',
    emphasisContainer:
      'border-primary-300 bg-primary-100 ring-1 ring-primary-300/60',
    badge: 'bg-primary-600',
  },
}

export const StatusBanner = ({
  variant = 'info',
  title,
  children,
  action,
  onDismiss,
  emphasis = false,
  className,
  ...props
}: StatusBannerProps) => {
  const t = useTranslations()
  const Icon = ICON_BY_VARIANT[variant]
  const { container, icon, emphasisContainer, badge } =
    COLOR_BY_VARIANT[variant]

  return (
    <output
      role="status"
      {...props}
      className={cx(
        'flex flex-col gap-4 rounded-xl border sm:flex-row sm:items-center sm:justify-between',
        emphasis
          ? cx('p-6 shadow-sm', emphasisContainer)
          : cx('p-5', container),
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {emphasis ? (
          <span
            className={cx(
              'flex size-9 shrink-0 items-center justify-center rounded-full',
              badge,
            )}
          >
            <Icon className="size-5 text-white" weight="fill" aria-hidden />
          </span>
        ) : (
          <Icon
            className={cx('size-5 shrink-0', icon)}
            weight="fill"
            aria-hidden
          />
        )}
        <div className="min-w-0 flex-1 space-y-1 text-sm leading-relaxed text-slate-600">
          {title ? (
            <p
              className={cx(
                'font-semibold text-slate-800',
                emphasis && 'text-base',
              )}
            >
              {title}
            </p>
          ) : null}
          {children}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-white/70 hover:text-slate-600"
          aria-label={t('Common.close')}
        >
          <XIcon className="size-4" />
        </button>
      ) : null}
    </output>
  )
}
